import type { Driver, DriveStep } from 'driver.js';

/**
 * Watches the DOM for a selector to appear or disappear, invoking onMatch
 * once. Used to gate tour steps on real user interactions that don't have a
 * single DOM element to attach a click listener to (e.g. a multi-step wizard
 * closing, or a control mounting once a session connects).
 */
export function waitForDom(selector: string, mode: 'appear' | 'disappear', onMatch: () => void): () => void {
  const matches = () => document.querySelector(selector) !== null;
  if (mode === 'appear' ? matches() : !matches()) {
    const id = window.setTimeout(onMatch, 0);
    return () => window.clearTimeout(id);
  }
  const observer = new MutationObserver(() => {
    if (mode === 'appear' ? matches() : !matches()) {
      observer.disconnect();
      onMatch();
    }
  });
  // `attributes` (not just `childList`) so a selector keyed off a class
  // toggle (e.g. an already-mounted tab becoming active) is caught too, not
  // just elements being added/removed from the DOM. `data-tour-phase` is
  // also watched — GeminiDirectStreamPage.tsx flips it on #tour-classroom-scene
  // as the roleplay session progresses (idle -> pre-connect -> connecting ->
  // greeting/live), used to gate steps on a phase change rather than a class.
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['class', 'data-tour-phase'],
  });
  return () => observer.disconnect();
}

/**
 * Builds a set of click/DOM-based advance gates that all share one
 * cleanupByStep map, keyed by step index. Each gated step's
 * onHighlightStarted stores a cleanup fn here; onDeselected (fires on every
 * step transition, including Escape/overlay-click/rapid re-highlight) looks
 * it up, invokes, and deletes it — shared across gate types so nothing leaks
 * a listener or observer on skip or early dismissal.
 *
 * driverRef is populated by the caller after the Driver instance is created
 * (steps must exist before driver() can be constructed), so gates read it
 * lazily inside their handlers rather than at build time.
 */
export function createGates(driverRef: React.RefObject<Driver | null>) {
  const cleanupByStep = new Map<number, () => void>();

  function gateOnClick(stepIndex: number, selector: string): Pick<DriveStep, 'onHighlightStarted' | 'onDeselected'> {
    return {
      onHighlightStarted: () => {
        const el = document.querySelector(selector);
        if (!el) return;
        const handler = () => driverRef.current?.moveNext();
        el.addEventListener('click', handler);
        cleanupByStep.set(stepIndex, () => el.removeEventListener('click', handler));
      },
      onDeselected: () => {
        cleanupByStep.get(stepIndex)?.();
        cleanupByStep.delete(stepIndex);
      },
    };
  }

  // Click gate that auto-advances instead of waiting forever when its target
  // isn't in the DOM at all — used for optional UI (e.g. the Step Guide
  // toggle, absent for scenarios with no configured teaching steps). driver.js
  // still fires onHighlightStarted with a missing element (it falls back to a
  // centered dummy target), so this can detect "genuinely absent" and move on
  // immediately, mirroring the auto-skip already done via
  // gateOnDom(..., 'disappear') for steps whose target never existed.
  function gateOnClickOrSkip(
    stepIndex: number,
    selector: string
  ): Pick<DriveStep, 'onHighlightStarted' | 'onDeselected'> {
    return {
      onHighlightStarted: () => {
        const el = document.querySelector(selector);
        if (!el) {
          const id = window.setTimeout(() => driverRef.current?.moveNext(), 0);
          cleanupByStep.set(stepIndex, () => window.clearTimeout(id));
          return;
        }
        const handler = () => driverRef.current?.moveNext();
        el.addEventListener('click', handler);
        cleanupByStep.set(stepIndex, () => el.removeEventListener('click', handler));
      },
      onDeselected: () => {
        cleanupByStep.get(stepIndex)?.();
        cleanupByStep.delete(stepIndex);
      },
    };
  }

  // Click gate that only advances once a second, related element has
  // appeared — used for "End Session", where clicking opens the Session
  // Ended modal a render cycle later. Without waiting, driver.js would try
  // to highlight the next step's target before it exists.
  function gateOnClickThenAppear(
    stepIndex: number,
    clickSelector: string,
    appearSelector: string
  ): Pick<DriveStep, 'onHighlightStarted' | 'onDeselected'> {
    return {
      onHighlightStarted: () => {
        const el = document.querySelector(clickSelector);
        if (!el) return;
        let cleanupAppear: (() => void) | null = null;
        const handleClick = () => {
          el.removeEventListener('click', handleClick);
          cleanupAppear = waitForDom(appearSelector, 'appear', () => driverRef.current?.moveNext());
        };
        el.addEventListener('click', handleClick);
        cleanupByStep.set(stepIndex, () => {
          el.removeEventListener('click', handleClick);
          cleanupAppear?.();
        });
      },
      onDeselected: () => {
        cleanupByStep.get(stepIndex)?.();
        cleanupByStep.delete(stepIndex);
      },
    };
  }

  function gateOnDom(
    stepIndex: number,
    selector: string,
    mode: 'appear' | 'disappear'
  ): Pick<DriveStep, 'onHighlightStarted' | 'onDeselected'> {
    return {
      onHighlightStarted: () => {
        cleanupByStep.set(
          stepIndex,
          waitForDom(selector, mode, () => driverRef.current?.moveNext())
        );
      },
      onDeselected: () => {
        cleanupByStep.get(stepIndex)?.();
        cleanupByStep.delete(stepIndex);
      },
    };
  }

  // DOM-appear gate that skips immediately instead of waiting, when a
  // boolean flag exposed via a data attribute already reads "true" — used
  // when the interaction this step would otherwise wait for (e.g. an avatar
  // click) is never going to happen at all for the current flow (e.g. an
  // auto-start scenario), as opposed to gateOnClickOrSkip's "target element
  // genuinely absent from the DOM" check. The flag must be decided
  // synchronously, at a point that can't itself race with this step
  // highlighting (see data-tour-auto-start in GeminiDirectStreamPage.tsx,
  // set in the same render that closes the scenario selector) — otherwise
  // this has the same false-skip risk gateOnDom's plain 'disappear' mode has
  // for a flag that's merely slow to flip on, not truly never going to.
  function gateOnDomUnlessFlag(
    stepIndex: number,
    flagSelector: string,
    flagAttribute: string,
    waitSelector: string
  ): Pick<DriveStep, 'onHighlightStarted' | 'onDeselected'> {
    return {
      onHighlightStarted: () => {
        const flagEl = document.querySelector(flagSelector);
        if (flagEl?.getAttribute(flagAttribute) === 'true') {
          const id = window.setTimeout(() => driverRef.current?.moveNext(), 0);
          cleanupByStep.set(stepIndex, () => window.clearTimeout(id));
          return;
        }
        cleanupByStep.set(
          stepIndex,
          waitForDom(waitSelector, 'appear', () => driverRef.current?.moveNext())
        );
      },
      onDeselected: () => {
        cleanupByStep.get(stepIndex)?.();
        cleanupByStep.delete(stepIndex);
      },
    };
  }

  return { gateOnClick, gateOnClickOrSkip, gateOnClickThenAppear, gateOnDom, gateOnDomUnlessFlag };
}
